import { Flex, Paper, Text, Button, Box } from "@mantine/core";
import { IconFileText, IconDownload } from "@tabler/icons-react";
import { getFileUrl } from "@/shared/utils/useFileUrl";
import {
  PROFILE_SECTION_HEADER_PX,
  PROFILE_SECTION_HEADER_PY,
} from "@/shared/styles/profileSectionHeader";

interface DocumentsTabProps {
  company: any;
  isMatchedView?: boolean;
  /** Renders body only (no outer card/header); used inside ProfileCollapsibleSection */
  embedded?: boolean;
}

export default function DocumentsTab({ company, isMatchedView = false, embedded = false }: DocumentsTabProps) {
  const documents = company.documents || [];

  const handleViewDocument = async (document: any) => {
    if (!document?.id) {
      console.error("Document ID not found");
      return;
    }

    try {
      const url = await getFileUrl(document.id, true);
      if (url) {
        window.open(url, "_blank");
      }
    } catch (err) {
      console.error("Error downloading document:", err);
    }
  };

  const documentsBody = (
    <Flex direction="column" gap="md">
          {documents.length === 0 ? (
            <Paper p="xl" radius="md" style={{ border: "1px solid #E5E7EB", backgroundColor: "white" }}>
              <Text c="slate.5" ta="center">
                No documents available
              </Text>
            </Paper>
          ) : (
            documents.map((document: any, index: number) => (
              <Paper
                key={document.id}
                p={{ base: "md", sm: "lg" }}
                radius="lg"
                bg="#F4F4F533"
                style={{ border: "1px solid #E5E7EB" }}
              >
                <Flex align="center" justify="space-between" gap="md">
                  <Flex align="center" gap="md" style={{ flex: 1 }}>
                    <Flex
                      w={{ base: 36, sm: 40 }}
                      h={{ base: 36, sm: 40 }}
                      style={{
                        borderRadius: 8,
                        backgroundColor: "#F9FAFB",
                        flexShrink: 0,
                      }}
                      align="center"
                      justify="center"
                    >
                      <IconFileText size={20} color="#6B7280" />
                    </Flex>

                    <Flex direction="column" style={{ flex: 1 }}>
                      <Text fz={{ base: "sm", sm: "md" }} fw={500} mb={2}>
                        {isMatchedView ? (document.title || document.name || "Untitled Document") : `Document ${index + 1}`}
                      </Text>
                      <Text fz={{ base: "xs", sm: "sm" }} c="slate.5">
                        {document.createdAt || document.uploadedAt
                          ? `Uploaded on ${new Date(document.createdAt || document.uploadedAt).toLocaleDateString("en-GB", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}`
                          : document.subtitle || document.description || ""}
                      </Text>
                    </Flex>
                  </Flex>

                  {isMatchedView && (
                    <Button
                      variant="subtle"
                      color="primary.7"
                      size="sm"
                      leftSection={<IconDownload size={16} />}
                      onClick={() => handleViewDocument(document)}
                    >
                      Download
                    </Button>
                  )}
                </Flex>
              </Paper>
            ))
          )}
    </Flex>
  );

  if (embedded) {
    return documentsBody;
  }

  return (
    <Paper
      p={0}
      radius="lg"
      style={{
        overflow: "hidden",
        backgroundColor: "#ebf0ff",
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
      }}
    >
      <Flex
        align="center"
        gap="xs"
        px={PROFILE_SECTION_HEADER_PX}
        py={PROFILE_SECTION_HEADER_PY}
        style={{ backgroundColor: "#04305c" }}
      >
        <IconFileText size={18} color="#ffc300" stroke={1.5} />
        <Text fw={700} fz="sm" c="#ffc300" style={{ letterSpacing: 0.2, lineHeight: 1.2 }}>
          Documents
        </Text>
      </Flex>
      <Box p={{ base: "lg", sm: "xl" }}>{documentsBody}</Box>
    </Paper>
  );
}
